import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaAngleLeft, FaNoteSticky, FaClock, FaSpinner } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { postSaveMood, postSyncUserActivities } from "../../api/mainApi";

const TAG_STYLES = {
  amber: "bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.8)]",
  emerald: "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]",
  cyan: "bg-cyan-400 shadow-[0_0_6px_rgba(34,211,238,0.8)]",
  purple: "bg-purple-400 shadow-[0_0_6px_rgba(192,132,252,0.8)]",
  rose: "bg-rose-400 shadow-[0_0_6px_rgba(251,113,133,0.8)]",
};

function MoodResult() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  /* ---------- 1. Стан --------------------------------------------------- */
  const [selectedMood, setSelectedMood] = useState(null);
  const [selectedAdditionalMood, setSelectedAdditionalMood] = useState(null);
  const [text, setText] = useState("");
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [followUpItem, setFollowUpItem] = useState(null);
  const [chosenOption, setChosenOption] = useState(null);
  const [randomPraise, setRandomPraise] = useState("");

  /* ---------- 2. Відновлюємо з localStorage ---------------------------- */
  useEffect(() => {
    const mood = localStorage.getItem("selectedMood");
    const addMood = localStorage.getItem("selectedAdditionalMood");
    const note = localStorage.getItem("moodNote");
    const achievement = localStorage.getItem("achievement");

    if (!mood && achievement === "true") {
      navigate("/achievement");
      return;
    }
    if (!mood) {
      navigate("/");
      return;
    }

    const moodNumber = Number(mood);
    setSelectedMood(moodNumber);
    if (addMood) setSelectedAdditionalMood(Number(addMood));
    if (note) {
      setText(note);
    }

    const savedSelectedAct = localStorage.getItem("selectedActivities");
    const savedAllAct = localStorage.getItem("moodActivities");
    if (savedSelectedAct && savedAllAct) {
      try {
        const parsedSelected = JSON.parse(savedSelectedAct);
        const parsedAll = JSON.parse(savedAllAct);
        const matched = parsedSelected
          .map((id) => parsedAll.find((a) => a.id === id))
          .filter(Boolean);
        setActivities(matched);
      } catch (e) {
        console.error("Error parsing activities", e);
      }
    }

    const praises = t(`praiseMood.${moodNumber}`, { returnObjects: true });
    if (Array.isArray(praises) && praises.length > 0) {
      const idx = Math.floor(Math.random() * praises.length);
      setRandomPraise(praises[idx]);
    }
  }, [navigate, t]);

  /* ---------- 3. Обираємо випадкове follow-up питання ------------------- */
  useEffect(() => {
    if (selectedMood && text === "") {
      const allFollowUps = t(`moodFollowUp.${selectedMood}`, {
        returnObjects: true,
      });
      if (Array.isArray(allFollowUps) && allFollowUps.length > 0) {
        const randomIndex = Math.floor(Math.random() * allFollowUps.length);
        setFollowUpItem(allFollowUps[randomIndex]);
      }
    } else {
      setFollowUpItem(null);
      setChosenOption(null);
    }
  }, [selectedMood, text, t]);

  /* ---------- 4. Допоміжні функції ------------------------------------ */
  const imageSrc = selectedMood
    ? selectedAdditionalMood
      ? `/${selectedMood}.${selectedAdditionalMood}.webp`
      : `/${selectedMood}.webp`
    : "";

  const moodKey = selectedMood
    ? selectedAdditionalMood
      ? `moods.${selectedMood}${selectedAdditionalMood}`
      : `moods.${selectedMood}`
    : "";

  async function handleFinish() {
    if (isLoading) return;
    setIsLoading(true);

    const moodNote = text?.trim() || null;
    const followUp =
      !moodNote && followUpItem && chosenOption
        ? { question: followUpItem.question, option: chosenOption }
        : null;

    const activitiesToSend = activities.filter(Boolean).map((act) => {
      const validText = act.tKey ? t(act.tKey) : act.text || "Кастомна подія";

      return {
        text: validText,
        isPrivate: Boolean(act.isPrivate),
        color: act.color || "emerald",
      };
    });

    try {
      // ІЗОЛЬОВАНИЙ БЛОК: Синхронізація всієї галявини
      if (localStorage.getItem("moodActivitiesChanged") === "true") {
        try {
          const allActivitiesRaw = localStorage.getItem("moodActivities");
          if (allActivitiesRaw) {
            const allActivities = JSON.parse(allActivitiesRaw);
            await postSyncUserActivities(allActivities);
          }
          localStorage.removeItem("moodActivitiesChanged");
        } catch (syncError) {
          console.error(
            "Фонова синхронізація не вдалася, але продовжуємо збереження настрою:",
            syncError,
          );
        }
      }

      // ОСНОВНИЙ БЛОК: Збереження щоденного запису
      const result = await postSaveMood(
        selectedMood,
        selectedAdditionalMood,
        moodNote,
        followUp,
        activitiesToSend,
      );

      // Очищення виконується тільки після успішного збереження настрою
      localStorage.removeItem("selectedMood");
      localStorage.removeItem("selectedAdditionalMood");
      localStorage.removeItem("moodNote");
      localStorage.removeItem("achievement");
      localStorage.removeItem("selectedActivities");

      if (result.achievement !== null) {
        localStorage.setItem("achievement", result.achievement);
      }

      if (
        String(localStorage.getItem("dayStreak")) !== String(result.day_streak)
      ) {
        navigate("/mood-streak", { state: { dayStreak: result.day_streak } });
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Error finalizing mood save flow:", error);
      setIsLoading(false);
    }
  }

  function getCurrentDateTime() {
    const now = new Date();
    // Виправлено: доповнюємо нулем, а не двійкою
    const pad = (n) => String(n).padStart(2, "0");

    return (
      `${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${now.getFullYear()} ` +
      `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    );
  }

  /* ---------- 5. Рендер ----------------------------------------------- */
  return (
    <div className="flex flex-col flex-1 h-full overflow-hidden bg-[#061014] relative select-none font-sans text-white -m-4 p-4">
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      {/* --- ФОН --- */}
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          background:
            "radial-gradient(circle at 50% 100%, oklch(30.2% 0.056 229.695) 0%, #061014 60%)",
        }}
      />
      <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] pointer-events-none z-0"></div>

      {/* --- Header --- */}
      <div className="relative z-50 w-full pt-6 pb-2 px-20">
        <p className="text-2xl font-semibold text-center text-white truncate w-full">
          {t("result.title")}
        </p>
      </div>

      <div className="relative z-10 flex flex-col flex-1 space-y-5 overflow-y-auto custom-scrollbar pb-6">
        <p className="text-sm text-center text-emerald-100/40">
          <FaClock className="inline -mt-px w-3 h-3 me-2 -mt-1" />
          {getCurrentDateTime()}
        </p>

        {/* --- Зображення + назва настрою --- */}
        {selectedMood && (
          <div className="w-full">
            <div className="flex flex-col items-center">
              <img
                src={imageSrc}
                alt="Mood"
                className="h-28 object-contain rounded-full drop-shadow-[0_10px_20px_rgba(0,0,0,0.5)] mb-3"
              />
              <div className="text-center text-3xl font-bold drop-shadow-md">
                {t(moodKey)}
              </div>
            </div>
          </div>
        )}

        {/* --- Похвала --- */}
        {randomPraise && (
          <p className="text-center font-semibold text-sm text-emerald-200/60 px-4">
            {randomPraise}
          </p>
        )}

        {/* --- ГОРИЗОНТАЛЬНА ПРОКРУТКА ДІЙ --- */}
        {activities.length > 0 && (
          <div className="w-full px-2">
            <div
              className="flex flex-nowrap overflow-x-auto gap-2 px-4 hide-scrollbar py-2"
              style={{
                WebkitMaskImage:
                  "linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)",
                maskImage:
                  "linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)",
              }}
            >
              {activities.map((act) => {
                const dotStyle = TAG_STYLES[act.color] || TAG_STYLES.emerald;
                const displayText = act.tKey ? t(act.tKey) : act.text;

                return (
                  <div
                    key={act.id}
                    className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 backdrop-blur-sm rounded-xl shadow-sm"
                  >
                    <div className={`w-1.5 h-1.5 rounded-full ${dotStyle}`} />
                    <span className="text-[13px] text-gray-200 font-medium">
                      {displayText}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* --- Нотатка (Glassmorphism + виправлення багу з текстом) --- */}
        {text && (
          <div className="px-4">
            <div className="relative bg-white/5 border border-emerald-900/50 p-5 pt-8 rounded-3xl shadow-inner backdrop-blur-md">
              <span className="absolute text-xs left-5 top-3 text-emerald-200/50 flex items-center gap-1 font-semibold uppercase tracking-wider">
                <FaNoteSticky className="w-3 h-3" />
                {t("moods.note")}
              </span>
              <p className="text-white text-base leading-relaxed break-words break-all whitespace-pre-wrap">
                {text}
              </p>
            </div>
          </div>
        )}

        {/* --- Опитування --- */}
        {!text && followUpItem && (
          <div className="px-4 pb-2">
            <p className="mb-4 text-base font-medium text-center text-emerald-50">
              {followUpItem.question}
            </p>
            <div className="flex flex-col space-y-2">
              {followUpItem.options.map((opt, idx) => {
                const isSelected = chosenOption === opt;
                return (
                  <button
                    key={idx}
                    onClick={() => {
                      if (isSelected) setChosenOption(null);
                      else setChosenOption(opt);
                    }}
                    className={`flex items-center py-3 px-4 rounded-2xl border transition-all duration-300 ${
                      isSelected
                        ? "bg-emerald-500/20 border-emerald-400 shadow-[inset_0_0_15px_rgba(16,185,129,0.2)]"
                        : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 mr-3 rounded-full border-2 transition-colors ${
                        isSelected
                          ? "bg-emerald-400 border-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]"
                          : "border-gray-500"
                      }`}
                    />
                    <span
                      className={`text-sm font-medium ${isSelected ? "text-emerald-50" : "text-gray-300"}`}
                    >
                      {opt}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* --- НИЖНІЙ БЛОК --- */}
      <div className="relative z-20 mt-auto pt-4 pb-2">
        <button
          onClick={handleFinish}
          disabled={isLoading}
          className="text-2xl font-bold rounded-lg py-3 w-full transition-all duration-300 bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 bg-gradient-size animate-gradient-move text-white shadow-[0_0_20px_rgba(16,185,129,0.3)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isLoading ? (
            <FaSpinner className="animate-spin w-7 h-7" />
          ) : (
            t("moods.save")
          )}
        </button>
      </div>
    </div>
  );
}

export default MoodResult;
